import cl from 'classnames'
import styles from '@features/occupationalRisks/Modal.module.scss'
import { Modal } from '@/components/Modal'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { LoadingScreen } from '@/components/Loader'
import {
	TabUnstyled as Tab,
	TabsListUnstyled as TabsList,
	TabsUnstyled as Tabs,
	TabPanelUnstyled as TabPanel,
} from '@mui/base'
import { Tree } from 'antd'
import { Button } from '@/ui'
import { getCurrentLanguage } from '@/i18next'
import { LANGUAGE_ENUM, useDebounce } from '@/shared'
import { Input } from '@/components/Input/Input'
import SearchIcon from '@mui/icons-material/Search'

export const HarmfulModal = ({
															 item,
															 onClose,
															 onSubmit,
														 }) => {
	const { t } = useTranslation()
	const lang = getCurrentLanguage()

	const { harmful } = useSelector((state) => ({
		harmful: state.equipment1.harmful,
	}))

	const [searchValue, setSearchValue] = useState('')
	const [selectedCheckbox, setSelectedCheckbox] = useState({})
	const [activeTab, setActiveTab] = useState(null)
	const [expandedKeys, setExpandedKeys] = useState([])

	const [tabs, setTabs] = useState({ columns: [], data: [] })

	const debouncedSearchValue = useDebounce(searchValue, 500)

	const setData = useCallback((array) => {
		return array.map(obj => {
			let label = {
				[LANGUAGE_ENUM.rus]: obj.nameRu,
				[LANGUAGE_ENUM.kz]: obj.nameKz,
			}

			return {
				key: obj.code,
				title: label[lang],
				children: setData(obj.child),
			}
		})
	}, [lang])

	useEffect(() => {
		if (harmful.data) {
			const array = setData(harmful.data)

			setTabs({ data: array, columns: array })
		}
	}, [harmful.data])

	useEffect(() => {
		if (tabs.columns.length) {
			setActiveTab(tabs?.columns[0]?.key)

			let arrayOfCode = item?.child?.dicts?.harmfulCodes?.map(i => i.code)
			let arrayOfType = {}

			arrayOfCode.forEach(item => {
				const key = item[0] + '.'
				arrayOfType[key] = arrayOfType[key] || []
				arrayOfType[key].push(item)
			})

			setSelectedCheckbox(arrayOfType)
		}
	}, [tabs?.columns])

	const handleChangeTab = (e, newValue) => {
		setActiveTab(newValue)
		setSearchValue('')
		setExpandedKeys([])
		setTabs(prevState => ({ ...prevState, data: prevState.columns }))
	}

	const handleTreeCheck = (checkedKeys, info, tabKey) => {
		setSelectedCheckbox(prevState => {
			const prevSelectedKeys = prevState[tabKey] || []
			const newCheckedKeys = info?.checkedNodes?.filter(i => !i.children.length)?.map(i => i.key) || []

			let updatedKeys

			if(searchValue){
				const keysToAdd = newCheckedKeys.filter(key => !prevSelectedKeys.includes(key))
				updatedKeys = [
					...prevSelectedKeys,
					...keysToAdd,
				]
			} else {
				const newCheckedSet = new Set(newCheckedKeys)
				const prevSelectedSet = new Set(prevSelectedKeys)

				updatedKeys = Array.from(
					new Set([
						...prevSelectedKeys.filter(key => newCheckedSet.has(key)),
						...newCheckedKeys.filter(key => !prevSelectedSet.has(key))
					])
				)
			}

			return {
				...prevState,
				[tabKey]: updatedKeys,
			}
		})
	}

	const handleSubmitButton = () => {
		let res = Object.values(selectedCheckbox)?.flat() || []
		onSubmit(res)
	}

	const filterByValue = useCallback((data, searchText) => {
		const lowerSearchText = searchText.trim().toLowerCase()
		if (!lowerSearchText) {
			return data
		}
		const filterRecursive = (recursiveItem) => {
			const matches = recursiveItem.title && recursiveItem.title.toLowerCase().includes(lowerSearchText)
			if (matches) {
				return {
					...recursiveItem,
					children: recursiveItem.children || [],
					isSearched: true,
				}
			}
			const filteredChildren = recursiveItem.children
				? recursiveItem.children.map(filterRecursive).filter(child => child !== null)
				: []
			if (filteredChildren.length > 0) {
				return {
					...recursiveItem,
					children: filteredChildren,
					isSearched: false,
				}
			}
			return null
		}

		return data.map(filterRecursive).filter(recursiveItem => recursiveItem !== null)
	}, [])

	const expandedKeyFunc = useCallback((data) => {
		const keys = []

		const collectKeys = (item, parentKeys = []) => {
			const currentKeys = item.key ? [...parentKeys, item.key] : parentKeys

			if (item.isSearched === true && item.key) {
				keys.push(...currentKeys)
			}

			if (item.children && Array.isArray(item.children)) {
				item.children.forEach(child => collectKeys(child, currentKeys))
			}
		}

		data.forEach(item => collectKeys(item, []))
		return [...new Set(keys)]
	}, [])

	useEffect(() => {
		const currentTab = tabs.data?.find(i => i.key === activeTab)

		if(searchValue){
			const result = filterByValue(currentTab.children, searchValue)
			setTabs(prevState => {
				const tabIndex = prevState.data.findIndex(tab => tab.key === currentTab.key)
				if (tabIndex === -1) {
					return {
						...prevState,
						data: [...prevState.data, { ...currentTab, children: result }]
					}
				}
				const updatedData = [...prevState.data]
				updatedData[tabIndex] = { ...currentTab, children: result }
				return {
					...prevState,
					data: updatedData
				}
			})

			setExpandedKeys(expandedKeyFunc(result))
		} else {
			setTabs(prevState => ({...prevState, data: prevState.columns}))
		}
	}, [debouncedSearchValue])

	const renderTreeData = useCallback((data) => {
		return <span className={cl({[styles.coloredTreeData]: data.isSearched})}>{data.key} {data.title}</span>
	}, [])

	return (
		<Modal windowClassName={cl(styles.modal_wrapper, styles.modal_wrapperSizeM)}>
			<h4>
				{t('occupational_risks_assessment.add_harmful')}
			</h4>

			<div className={cl(styles.wrapper)}>
				{harmful.loading
					? <LoadingScreen />
					: (
						<div className={cl('mt-4')}>
							<Tabs value={activeTab} onChange={handleChangeTab} className={cl(styles.tab)}>
								<TabsList className={cl('flex', 'flex-nowrap', 'scrollHorizontal')} style={{ overflowX: 'scroll' }}>
									{tabs.columns?.map((item) => (
										<Tab value={item?.key} key={item?.key} id={item.key} className={styles.tab__item}>
										<span className={cl('text-nowrap')}>
											{item?.title}
										</span>
										</Tab>
									))}
								</TabsList>

								<div className={cl('mt-4')}>
									<Input value={searchValue} prefix={<SearchIcon />} onChange={(e) => {
										setSearchValue(e.target.value)
									}} />
								</div>

								<div className={cl('mt-4')}>
									{tabs.data.map((item) => {
										return (
											<TabPanel key={item.key} value={item.key}>
												<div className={styles.container}>
													{item.children && item.children?.length > 0 ? (
														<Tree
															checkable
															showLine
															onCheck={(checkedKeys, info) => handleTreeCheck(checkedKeys, info, item.key)}
															treeData={item.children}
															checkedKeys={selectedCheckbox[item.key] || []}
															onExpand={(expandedKeys, {expanded: bool, node}) => setExpandedKeys(expandedKeys)}
															expandedKeys={expandedKeys}
															titleRender={(nodeData) => renderTreeData(nodeData)}
														/>
													) : (searchValue && !expandedKeys.length && (
														<h1></h1>
													))}

												</div>
											</TabPanel>
										)
									})}
								</div>
							</Tabs>

							<div className={cl('flex', 'justify-content-end', 'gap-4', 'mt-4')}>
								<Button type="bare" onClick={onClose}>
									{t('cancel')}
								</Button>

								<Button onClick={handleSubmitButton}
												// disabled={!Object.values(selectedCheckbox)?.some(arr => arr?.length > 0)}
								>
									{t('add')}
								</Button>
							</div>
						</div>
					)}
			</div>
		</Modal>
	)
}